import { observer } from 'mobx-react';
import { FC, useCallback, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import { mdfAndExclModalStore } from '../../store';
import { localeStore } from 'src/mobx/localesStore';
import { modalStore } from 'src/mobx/modalStore';
import { IProductV4Exclusion, IProductV4Modifier } from 'src/types/product';
import { getTranslate } from 'src/utils/helpers';

interface IProps {
  item?: IProductV4Modifier | IProductV4Exclusion;
}

const ModifierAndExclusionModalItem: FC<IProps> = observer(({ item }: IProps): JSX.Element => {
  const { isModifiersSection, selectedModifiers, selectedExclusions, isSelectOnlyOneItemAvailable, disableModifierWithoutOptions } =
    mdfAndExclModalStore;

  const isWithoutOptions = useMemo((): boolean => {
    if (!isModifiersSection || !item || !disableModifierWithoutOptions) return false;

    return !(item as IProductV4Modifier).options.filter((option) => !option.is_delete).length;
  }, [item, isModifiersSection, disableModifierWithoutOptions]);

  const isNoAvailable = useMemo((): boolean => {
    if (!isSelectOnlyOneItemAvailable || !item) return false;

    if (isModifiersSection) {
      return Boolean(modalStore.modifierAndExclusionModal?.selectedModifiers.find((modifier) => modifier.id === item.id));
    } else {
      return Boolean(modalStore.modifierAndExclusionModal?.selectedExclusions.find((exclusion) => exclusion.id === item.id));
    }
  }, [modalStore.modifierAndExclusionModal, isModifiersSection, isSelectOnlyOneItemAvailable, item]);

  const clickItem = useCallback(() => {
    if (isNoAvailable || isWithoutOptions || !item) return;
    mdfAndExclModalStore.selectItem(item);
  }, [item, isNoAvailable, isWithoutOptions]);

  const isActive = useMemo((): boolean => {
    if (!item) return false;

    if (isModifiersSection) {
      return selectedModifiers.some((modifier) => modifier.id === item.id);
    }

    return selectedExclusions.some((exclusion) => exclusion.id === item.id);
  }, [isModifiersSection, selectedModifiers, selectedExclusions, item]);

  const itemClassName = useMemo((): string => {
    let className: string = 'custom-modal__item mb-2 mr-2';

    if (isActive) className += ' custom-modal__item_active';
    if (isNoAvailable || isWithoutOptions) className += ' custom-modal__item_no-available';

    return className;
  }, [isActive, isNoAvailable, isWithoutOptions]);

  return (
    <>
      {item ? (
        <li className={itemClassName}>
          <div className="custom-modal__item__name-wrapper px-3" onClick={clickItem}>
            <p className="custom-modal__item__name">{getTranslate(item.name)}</p>
          </div>
          {isWithoutOptions ? (
            <div className="custom-modal__item__profile-name-wrapper cursor-not-allowed" onClick={clickItem}>
              <p className="custom-modal__item__profile-name letter-spacing-12 px-2 py-1 mr-3 cursor-not-allowed">{localeStore.t('no_options')}</p>
            </div>
          ) : null}
        </li>
      ) : (
        <div className="custom-modal__item custom-modal__item__skeleton mb-2 mr-2">
          <Skeleton width="100%" height="100%" />
        </div>
      )}
    </>
  );
});

export default ModifierAndExclusionModalItem;
